import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { removeQuizDupes, SortAndFilterStudentClasses, uniqueArray } from "../Helpers";
import axiosJwt from "../services/axios";
import Amplify from "aws-amplify";
import {
  Class,
  ClassExtended,
  ComputedQuizScore,
  CourseStructureCtx,
  DbUser,
  PostMessage,
  TeacherClassDetails,
  TeacherClassDetailsCourse,
  TempEventCtx,
  UserModuleProgress,
  UserSubmission,
  UserSubrole,
} from "../interfaces";

import { QuestionType, Role, SubRoles, TARGET_COMPLETION_STATUS } from "../enums";
import {
  FeatureFlag,
  Skill,
  UpdateUserRequest,
  User,
  UserClassSettings,
  UserFeatureFlag,
  UserMetadataData,
  UserProgressWithQuestion,
  UserSurvey,
  CourseMetadata,
  UserEventV2,
  UserQuizSubmission,
  QuestionMetadata,
  MaintenanceWindow,
  OrchardEventPayload,
} from "@backend-types";

import { cognitoConfig } from "../CognitoConfig";
import { formatISO } from "date-fns";
import { CourseStructureContext } from "../context/CourseStructureContext";
import { TempEventContext } from "./TempEventContext";
import { logger } from "../services/logger";
import {
  Question as QuestionTree,
  Module as ModuleTree,
  Target,
  Course,
} from "../components/utils/CourseTree";
import { IUseMacsAuthenticator } from "../hooks/useMacsAuthenticator";
import { useHistory, useLocation } from "react-router-dom";
import { ErrorToast } from "../components/v2/utils/Toast";
import * as Sentry from "@sentry/react";

Amplify.configure(cognitoConfig);

interface UserProviderProps {
  authHook: IUseMacsAuthenticator;
  children: React.ReactNode;
}

export const UserContext = createContext({});

export const UserProvider: React.FC<UserProviderProps> = ({ authHook, children }) => {
  const courseStructureCtx = useContext(CourseStructureContext) as CourseStructureCtx;
  const courseStructureCtxRef = useRef(courseStructureCtx);
  courseStructureCtxRef.current = courseStructureCtx;
  const history = useHistory();

  const tempEventCtx = useContext(TempEventContext) as TempEventCtx;
  const { authenticated, initialized } = authHook;

  const api = process.env.REACT_APP_BACKEND_API!;
  const [currentUserEndpointIsLoading, setCurrentUserEndpointIsLoading] = useState(false);
  const [tryAgainEndpointIsLoading, setTryAgainEndpointIsLoading] = useState(false);
  const [updateUserEndpointIsLoading, setUpdateUserEndpointIsLoading] = useState(false);
  const [refreshExplorePageData, setRefreshExplorePageData] = useState(false);
  const [
    patchUserLastAccessAtEndpointIsLoading,
    setPatchUserLastAccessAtEndpointIsLoading,
  ] = useState(false);
  const [featureFlagEndpointIsLoading, setFeatureFlagEndpointIsLoading] = useState(false);
  // All Modules progress for v2 design
  const [allModulesProgress, setAllModulesProgress] = useState<UserModuleProgress>({});
  const [dbUser, setDbUser] = useState<DbUser | undefined>();
  // text/video mode can change independenty of the "default" preference
  // separate temporary state required so we can revert mode to default when user leaves current GPP
  const [gppTextMode, setGppTextMode] = useState<boolean | undefined>(
    dbUser?.metadata?.le_screen_gpp_prefer_text
  );

  const [userStreak, setUserStreak] = useState<Date[]>([]);
  const [userSubmissions, setUserSubmissions] = useState<UserSubmission[]>();
  const [userQuizSubmissions, setUserQuizSubmissions] = useState<UserQuizSubmission[]>();
  // array of skill ID
  const [userSavedSkills, setUserSavedSkills] = useState<string[]>([]);

  const [questionsSubmitted, setQuestionsSubmitted] = useState<
    Array<string> | undefined
  >();
  const [tryAgainSubmissions, setTryAgainSubmissions] = useState<Set<string>>(new Set());

  const [DbFeatureFlags, setDbFeatureFlags] = useState<
    Map<number, FeatureFlag> | undefined
  >();
  const [maintenanceWindows, setMaintenanceWindows] = useState<MaintenanceWindow[]>([]);
  const [userQuestionStarted, setUserQuestionStarted] = useState<
    Array<Record<string, string>>
  >([]);

  const [upsertingUserProgress, setUpsertingUserProgress] = useState<boolean>(false);

  const [initializedQuestionLaunched, setInitializedQuestionLaunched] = useState(false);

  const [assetLockOverride, setAssetLockOverride] = useState(false);
  const location = useLocation();
  const currentQuestionId = new URLSearchParams(location.search).get("questionId");
  // Use to get the class the student is currently viewing
  const [studentCurrentClassId, setStudentCurrentClassId] = useState<string | null>(
    authHook.studentCurrentClassId
  );
  const studentCurrentClass = useMemo(() => {
    return dbUser?.class_student
      .map((item) => item.class)
      .find((item) => item?.id.toString() === studentCurrentClassId);
  }, [dbUser?.class_student, studentCurrentClassId]);

  const getAllTryAgainQuestions = useCallback(() => {
    const api = process.env.REACT_APP_BACKEND_API!;
    setTryAgainEndpointIsLoading(true);
    const userGetQuestionSubmitted = api + "/try-again";
    axiosJwt
      .get(userGetQuestionSubmitted)
      .then((res) => {
        if (res.data.try_again_submissions) {
          parseTryAgainString(res.data.try_again_submissions);
          setTryAgainEndpointIsLoading(false);
        }
      })
      .catch((err) => {
        logger.error(err);
        parseTryAgainString("");
        setTryAgainEndpointIsLoading(false);
      });
  }, []);
  const getAllTryAgainQuestionsRef = useRef(getAllTryAgainQuestions);

  const getDbUserRef = useRef<() => void>();
  const handleChangeStudentClass = (newClassId: number | null) => {
    if (newClassId?.toString() !== studentCurrentClass) {
      authHook.setStudentCurrentClassId(newClassId ? newClassId.toString() : null);
      setStudentCurrentClassId(newClassId ? newClassId.toString() : null);
      const reloadUser = async () => {
        // if (getDbUserRef.current) {
        //   getDbUserRef.current();
        // }
        // getAllTryAgainQuestionsRef.current();
        window.location.reload();
      };
      reloadUser();
    }
  };

  const getDbUser = () => {
    const api = process.env.REACT_APP_BACKEND_API!;
    const currentUserEndpoint = api + "/current-user";
    setCurrentUserEndpointIsLoading(true);
    axiosJwt
      .get(currentUserEndpoint)
      .then((res) => {
        const dbUserResponse = res.data as DbUser;
        setDbUser(dbUserResponse);
        const sentryUserId = String(dbUserResponse.id ?? dbUserResponse.lti_user_id);
        Sentry.setUser({
          id: sentryUserId,
          username: sentryUserId,
          role: dbUserResponse.role,
        } as Sentry.User & { role: string });
        const replay = Sentry.getClient()?.getIntegrationByName?.("Replay") as any;
        replay?.start();
        const questionsSubmittedResponse: Array<string> = [];
        const userSubmissionsResponse = dbUserResponse.user_submissions;
        userSubmissionsResponse?.forEach((submission: UserSubmission) => {
          questionsSubmittedResponse.push(submission.question_id);
        });
        const savedVideosResponse: string[] = dbUserResponse?.user_saved_skills.map(
          (skill: Skill) => {
            return skill.skill_id;
          }
        );
        const userQuizSubmissionsResponse = dbUserResponse.UserQuizSubmission;
        // check the student's classes and set their currently selected class automatically
        // also check if the student is actually in the class and update if not
        if (dbUserResponse.role === Role.student) {
          const usersClasses = SortAndFilterStudentClasses(dbUserResponse.class_student);
          if (!authHook.studentCurrentClassId) {
            if (usersClasses.length > 0 && usersClasses[0]) {
              handleChangeStudentClass(usersClasses[0].id);
            }
          } else {
            const classCheck = usersClasses.find(
              (item) => item?.id.toString() === authHook.studentCurrentClassId
            );
            if (!classCheck) {
              if (usersClasses.length > 0 && usersClasses[0]) {
                handleChangeStudentClass(usersClasses[0].id);
              } else {
                handleChangeStudentClass(null);
              }
            }
          }
        } else {
          handleChangeStudentClass(null);
        }
        setUserStreak(dbUserResponse.streaks);
        setQuestionsSubmitted(uniqueArray(questionsSubmittedResponse));
        setUserSubmissions(uniqueArray(userSubmissionsResponse));
        setUserQuizSubmissions(removeQuizDupes(uniqueArray(userQuizSubmissionsResponse)));
        setUserSavedSkills(savedVideosResponse);
        setUserQuestionStarted(dbUserResponse.user_question_start);
        setCurrentUserEndpointIsLoading(false);
        setGppTextMode(dbUserResponse?.metadata?.le_screen_gpp_prefer_text);
      })
      .catch((err) => logger.error(err));
  };
  getDbUserRef.current = getDbUser;

  useEffect(() => {
    // Cache feature flags regardless of auth status so we can utilize feature flags on login and self registration
    if (initialized) {
      getFeatureFlagsAndMaintenance();
    }

    if (!authenticated) return;
    const initializeUser = async () => {
      await patchDbUserLastAccessAt();
      if (getDbUserRef.current) {
        getDbUserRef.current();
      }
      getAllTryAgainQuestionsRef.current();
    };
    initializeUser();
  }, [initialized, authenticated]);

  const resetGppTextMode = () => {
    if (!dbUser) return;
    setGppTextMode(dbUser?.metadata?.le_screen_gpp_prefer_text);
    console.log(
      "Resetting GPP Text Mode to: ",
      dbUser?.metadata?.le_screen_gpp_prefer_text
    );
  };

  const patchDbUserLastAccessAt = async () => {
    let data = {
      last_access_at: formatISO(new Date()),
    };
    setPatchUserLastAccessAtEndpointIsLoading(true);
    try {
      await axiosJwt.patch("/user/last-access-at", data).then((res) => {
        if (res.data) {
          setUserStreak(res.data.streaks ?? []);
        }
      });
    } catch (err) {
      logger.error(err);
    }
    setPatchUserLastAccessAtEndpointIsLoading(false);
  };

  const updateDbUser = async (userId: String, data: UpdateUserRequest) => {
    try {
      setUpdateUserEndpointIsLoading(true);
      const response = await axiosJwt.put(`/user/${userId}`, data);
      setDbUser((prev) => ({ ...prev, ...response.data }));
    } catch (e) {
      logger.error(e);
    } finally {
      setUpdateUserEndpointIsLoading(true);
    }
  };

  const updateUserMetadata = async (metadata: Partial<UserMetadataData>) => {
    if (!dbUser) return;
    if (metadata.le_screen_gpp_prefer_text !== undefined) {
      setGppTextMode(metadata.le_screen_gpp_prefer_text);
    }
    return updateDbUser(dbUser.id, {
      metadata: { ...dbUser.metadata, ...metadata },
    });
  };

  // string must be delimited by ";;"
  const parseTryAgainString = (tryAgainString: string) => {
    try {
      const tryagainSubmissions = tryAgainString.split(";;");
      const tryagainSubmissionsSet: Set<string> = new Set();
      tryagainSubmissions.forEach((questionId: string) => {
        tryagainSubmissionsSet.add(questionId);
      });
      setTryAgainSubmissions(tryagainSubmissionsSet);
    } catch (e) {
      console.error(e);
    }
  };

  /** Returns an object representing the archived and active teacher classes
   * Separated by teacher classes, observing classes, and observing students
   */
  const getProfileClassDetails = useMemo((): TeacherClassDetails | undefined => {
    if (!dbUser) return undefined;
    const user = dbUser;
    const courseList: TeacherClassDetailsCourse[] = [];

    // always recent classes map to curerent user
    let recentlyViewed = user.UserClassSettings?.map((item) => {
      const classe = item.class;
      const teacherInfo = classe?.user as DbUser;
      const teacherSchoolInfo =
        teacherInfo?.user_school && teacherInfo?.user_school.length
          ? teacherInfo?.user_school[0].school
          : undefined;
      const courseInfo: TeacherClassDetailsCourse = {
        course_id: classe?.course_id ?? 1,
        course_name: classe?.course?.name ?? "",
        course_metadata: classe?.course?.metadata as CourseMetadata,
        course_modules: classe?.course?.modules?.map((module) => module.module_id) ?? [],
      };
      courseList.push(courseInfo);

      const classExtended: ClassExtended = {
        id: item.class_id,
        external_id: classe?.external_id ?? null,
        external_parent_id: classe?.external_parent_id ?? null,
        teacher_id: classe?.teacher_id ?? "",
        subject: classe?.course?.name ?? "",
        period_block: classe?.period_block ?? null,
        start_date: classe?.start_date ?? null,
        end_date: classe?.end_date ?? null,
        class_code: classe?.class_code ?? "",
        start_date_str: classe?.start_date_str ?? "",
        created_at: classe?.created_at ? new Date(classe?.created_at) : new Date(),
        updated_at: classe?.updated_at ? new Date(classe?.updated_at) : new Date(),
        updated_by_id: classe?.updated_by_id ?? "",
        is_tester: classe?.is_tester ?? false,
        is_demo: classe?.is_demo ?? false,

        user: classe?.user as User,
        teacher_name: teacherInfo?.full_name ?? "",
        teacher_email: teacherInfo?.email ?? "",
        school_id: `${teacherSchoolInfo?.id}`,
        school_name: teacherSchoolInfo?.name ?? "",
        settings: item,

        course_id: courseInfo.course_id,
        course_name: courseInfo.course_name,
        course_metadata: courseInfo.course_metadata,
        course_modules: courseInfo.course_modules.join(",") ?? "",
        metadata: classe?.metadata ?? {},
        student_view_class: classe?.student_view_class ?? false,
      };
      return classExtended;
    });

    recentlyViewed =
      recentlyViewed?.sort((a, b) => {
        const dateA =
          a.settings?.viewed_at instanceof Date
            ? a.settings.viewed_at.getTime()
            : a.settings?.viewed_at
            ? new Date(a.settings.viewed_at).getTime()
            : 0;
        const dateB =
          b.settings?.viewed_at instanceof Date
            ? b.settings.viewed_at.getTime()
            : b.settings?.viewed_at
            ? new Date(b.settings.viewed_at).getTime()
            : 0;
        return dateB - dateA; // descending order, most recent first
      }) ?? [];

    const classesExtended = user.class.map((classe) => {
      const courseInfo: TeacherClassDetailsCourse = {
        course_id: classe.course_id ?? 1,
        course_name: classe.course?.name ?? "",
        course_metadata: classe.course?.metadata as CourseMetadata,
        course_modules: classe.course?.modules?.map((module) => module.module_id) ?? [],
      };
      courseList.push(courseInfo);
      const classExtended: ClassExtended = {
        ...classe,

        user: user,
        teacher_name: user.full_name,
        teacher_email: user.email,
        teacher_id: user.id,
        school_id: user.school_id ?? "",
        school_name: user.school_name ?? "",
        settings: classe.UserClassSettings && classe.UserClassSettings[0],

        course_id: courseInfo.course_id,
        course_name: courseInfo.course_name,
        course_metadata: courseInfo.course_metadata,
        course_modules: courseInfo.course_modules.join(",") ?? "",
      };
      return classExtended;
    });

    // observed classes
    const classSubRoles: UserSubrole[] = user.user_subrole.filter(
      (item) => item.sub_role === SubRoles.OBSERVER_CLASS
    );
    const classesObserved = [] as Class[];
    classSubRoles.forEach((item) => {
      if (item.class) {
        classesObserved.push(item.class);
      }
    });
    const classesExtendedObserved = classesObserved.map((classe) => {
      const teacherInfo = classe.user as DbUser;
      const teacherSchoolInfo =
        teacherInfo?.user_school && teacherInfo?.user_school.length
          ? teacherInfo?.user_school[0].school
          : undefined;
      const courseInfo: TeacherClassDetailsCourse = {
        course_id: classe.course_id ?? 1,
        course_name: classe.course?.name ?? "",
        course_metadata: classe.course?.metadata as CourseMetadata,
        course_modules: classe.course?.modules?.map((module) => module.module_id) ?? [],
      };
      courseList.push(courseInfo);

      const classExtended: ClassExtended = {
        id: classe.id,
        external_id: classe.external_id,
        external_parent_id: classe?.external_parent_id ?? null,
        teacher_id: classe.teacher_id ?? "",
        subject: classe.course?.name!,
        period_block: classe.period_block,
        start_date: classe.start_date,
        end_date: classe.end_date,
        class_code: classe.class_code,
        start_date_str: classe.start_date_str,
        created_at: new Date(classe.created_at),
        updated_at: new Date(classe.updated_at),
        updated_by_id: classe.updated_by_id,
        is_tester: classe.is_tester ?? false,
        is_demo: classe.is_demo ?? false,

        user: classe.user as User,
        teacher_name: teacherInfo?.full_name ?? "",
        teacher_email: teacherInfo?.email ?? "",
        school_id: `${teacherSchoolInfo?.id}`,
        school_name: teacherSchoolInfo?.name ?? "",
        settings: classe.UserClassSettings && classe.UserClassSettings[0],

        course_id: courseInfo.course_id,
        course_name: courseInfo.course_name,
        course_metadata: courseInfo.course_metadata,
        course_modules: courseInfo.course_modules.join(",") ?? "",

        observer_ids: user.id,
        metadata: classe.metadata,
        student_view_class: classe.student_view_class,
      };

      return classExtended;
    });
    // observed students
    const studentsObserved = user.user_subrole.filter(
      (item) => item.sub_role === SubRoles.OBSERVER_STUDENT
    );

    return {
      courseList: courseList.filter((course, index, self) => {
        return index === self.findIndex((c) => c.course_id === course.course_id);
      }),
      recentlyViewed: recentlyViewed,
      active: {
        classes: classesExtended.filter(
          (item) =>
            !item.end_date || new Date(item.end_date).getTime() >= new Date().getTime()
        ),
        observingClasses: classesExtendedObserved.filter(
          (item) =>
            !item.end_date || new Date(item.end_date).getTime() >= new Date().getTime()
        ),
        observingStudents: studentsObserved.filter(
          (item) =>
            !item.class?.end_date ||
            new Date(item.class?.end_date).getTime() >= new Date().getTime()
        ),
      },
      archived: {
        classes: classesExtended.filter(
          (item) =>
            item.end_date && new Date(item.end_date).getTime() < new Date().getTime()
        ),
        observingClasses: classesExtendedObserved.filter(
          (item) =>
            item.end_date && new Date(item.end_date).getTime() < new Date().getTime()
        ),
        observingStudents: studentsObserved.filter(
          (item) =>
            item.class?.end_date &&
            new Date(item.class?.end_date).getTime() < new Date().getTime()
        ),
      },
    };
  }, [dbUser]);

  const upsertUserProgress = async (question_id: string) => {
    try {
      if (!upsertingUserProgress) {
        setUpsertingUserProgress(true);
        const result = await axiosJwt.post<UserProgressWithQuestion>("/user-progress", {
          question_id,
        });
        setUpsertingUserProgress(false);
        setDbUser((user) => ({ ...user!, user_progress: [result.data] }));
      }
    } catch (e) {
      setUpsertingUserProgress(false);
      logger.error(e);
    }
  };

  const upsertUserClassSettings = async (
    classId: number,
    data: Partial<UserClassSettings>
  ): Promise<UserClassSettings | undefined> => {
    try {
      const result = await axiosJwt.post<
        Partial<UserClassSettings>,
        { data: UserClassSettings }
      >(`/user-class-settings/${classId}`, data);
      return result.data;
    } catch (e) {
      logger.error(e);
      return undefined;
    }
  };

  const upsertUserSurvey = async (data: Partial<Omit<UserSurvey, "id" | "user_id">>) => {
    try {
      const result = await axiosJwt.post<UserSurvey>("/user-survey", {
        ...data,
      });
      setDbUser((user) => ({ ...user!, user_survey: result.data }));
    } catch (e) {
      logger.error(e);
    }
  };

  const insertUserTryAgain = async (submissionQuestionId: string, retry?: boolean) => {
    const userTryAgainEndpoint = api + "/try-again-add";
    const data = {
      question_id: submissionQuestionId,
    };

    try {
      const res = await axiosJwt.post(userTryAgainEndpoint, data);
      if (res.data && res.data.try_again_submissions !== undefined) {
        parseTryAgainString(res.data.try_again_submissions);
        console.debug(res);
      } else {
        const error = new Error("Failed to insert try again state");
        console.error(error);
        throw error;
      }
    } catch (err) {
      console.log(err);
      if (retry) {
        const error = new Error("Failed to insert try again state after retry");
        console.error(error);
        throw error;
      } else {
        await insertUserTryAgain(submissionQuestionId, true);
      }
    }
  };

  const deleteUserTryAgain = async (questionId: string, retry?: boolean) => {
    const userDeleteTryAgainEndpoint = api + "/try-again-remove";
    const data = {
      question_id: questionId,
    };
    try {
      const res = await axiosJwt.post(userDeleteTryAgainEndpoint, data);
      if (res.data && res.data.try_again_submissions !== undefined) {
        parseTryAgainString(res.data.try_again_submissions);
        console.debug(res);
      } else {
        const error = new Error("Failed to delete try again state");
        console.error(error);
        throw error;
      }
    } catch (err) {
      console.log(err);
      if (retry) {
        const error = new Error("Failed to delete try again state after retry");
        console.error(error);
        throw error;
      } else {
        await deleteUserTryAgain(questionId, true);
      }
    }
  };

  // refactored this so that it only triggers the grade passback once
  // was previously passing back a grade up to 4 times in a row
  const progressTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const insertUserProgressToCanvas = useCallback(
    (submissionQuestionId: string) => {
      // Clear any existing timeout to prevent multiple executions
      if (progressTimeoutRef.current) {
        clearTimeout(progressTimeoutRef.current);
        progressTimeoutRef.current = null;
      }
      progressTimeoutRef.current = setTimeout(() => {
        if (!courseStructureCtx.courseTreeStructure) return;
        let courseTreeStructure = courseStructureCtx.courseTreeStructure;
        const metadata = courseTreeStructure.getCurrentCourseMetadata();
        let question = courseTreeStructure?.getQuestion(submissionQuestionId);
        console.log("insertUserProgressToCanvas", submissionQuestionId, question);

        if (question) {
          let target = courseTreeStructure?.getTarget(question.target_id);
          if (metadata.deeplink_asset_type === "module") {
            let module = courseTreeStructure?.getModule(target?.module_id!);
            module?.upsertModuleProgress();
          } else if (metadata.deeplink_asset_type === "target") {
            target?.upsertTargetProgress();
          }
        }
      }, 1500);

      // Cleanup function
      return () => {
        if (progressTimeoutRef.current) {
          clearTimeout(progressTimeoutRef.current);
          progressTimeoutRef.current = null;
        }
      };
    },
    [courseStructureCtx.courseTreeStructure]
  );
  // Make sure to clear the timeout when the component unmounts
  useEffect(() => {
    return () => {
      if (progressTimeoutRef.current) {
        clearTimeout(progressTimeoutRef.current);
        progressTimeoutRef.current = null;
      }
    };
  }, []);

  // trigger this use effect only first time when the learning experience is loaded
  // this is done because if user navigated to some other target other than deeplink-ed asset for current session and submitted the question
  // so when the that target is called opened by deeplink then push the target progress to canvas in order to prevent user for resubmission.
  useEffect(() => {
    if (!courseStructureCtx.courseTreeStructure) return;
    if (!currentQuestionId || !courseStructureCtx.courseTreeProgressInitialized) return;
    const question =
      courseStructureCtx.courseTreeStructure?.getQuestion(currentQuestionId);

    const metadata = courseStructureCtx.courseTreeStructure?.getCurrentCourseMetadata();
    const deeplink_asset_type = metadata.deeplink_asset_type;

    if (question) {
      let target = courseStructureCtx.courseTreeStructure?.getTarget(question.target_id);

      if (deeplink_asset_type === "module") {
        let module = courseStructureCtx.courseTreeStructure?.getModule(
          target?.module_id!
        );
        if (module?.progress === 0) {
          console.log("No module progress");
          return;
        }
      } else if (deeplink_asset_type === "target") {
        if (target?.progress === 0) {
          console.log("No target progress");
          return;
        }
      }
    }

    if (!initializedQuestionLaunched) {
      insertUserProgressToCanvas(currentQuestionId);
      setInitializedQuestionLaunched(true);
    }
  }, [
    courseStructureCtx.courseTreeStructure,
    courseStructureCtx.courseTreeProgressInitialized,
    currentQuestionId,
    initializedQuestionLaunched,
    insertUserProgressToCanvas,
  ]);

  const insertUserSubmission = async (
    submissionQuestionId: string,
    submissionPoints: number,
    submissionMaxPoints: number,
    restoredSubmission?: boolean,
    retry?: boolean,
    v2?: boolean,
    suppressStreak?: boolean
  ): Promise<UserSubmission | undefined> => {
    const userSubmissionEndpoint = api + "/user-submission";
    const data = {
      question_id: submissionQuestionId,
      points: submissionPoints ?? 0,
      max_points: submissionMaxPoints ?? 10,
      restored_submission: restoredSubmission,
      ...(v2 && { version: "v2" }),
    };
    try {
      const res = await axiosJwt.post(userSubmissionEndpoint, data);
      if (res.data) {
        // insertUserProgressToCanvas(submissionQuestionId);
        // console.log("Got user submission response");
        if (res.data.updatedStreaks && !suppressStreak) {
          getUserStreak(res.data.updatedStreaks);
        }
        return res.data.createdUserSubmission;
      } else {
        return;
      }
    } catch (err) {
      logger.error(err);
      if (retry) {
        const error = new Error("Failed to insert user submission state after retry");
        console.error(error);
        throw error;
      } else {
        return await insertUserSubmission(
          submissionQuestionId,
          submissionPoints,
          submissionMaxPoints,
          restoredSubmission,
          true,
          v2,
          suppressStreak
        );
      }
    }
  };

  const insertUserEvents = (postMessages: PostMessage[]) => {
    const userEventEndpoint = api + "/user-event";
    postMessages.forEach((postMessage) => {
      const data = {
        question_id: postMessage.question_id,
        type: postMessage.type,
        post_message_id: postMessage.id,
        target: postMessage.target,
        status: postMessage.status,
        lo: postMessage.lo,
        levels: postMessage.levels,
        current_level: postMessage.currentLevel,
        attempts: postMessage.attempts,
        skill_id: postMessage.skillId,
      };
      axiosJwt.post(userEventEndpoint, data).catch((err) => logger.error(err));
    });
  };

  const insertUserEventsV2 = (postMessage: Omit<UserEventV2, "id" | "user_id">) => {
    const userEventEndpoint = api + "/user-event";
    const data = {
      version: "v2",
      question_id: postMessage.question_id,
      class_id: postMessage.class_id,
      submission_id: postMessage.submission_id,
      type: postMessage.type,
      contents: postMessage.contents,
      created_at: postMessage.created_at,
      learning_objectives: postMessage.learning_objectives,
      skill_ids: postMessage.skill_ids,
      status: postMessage.status,
      current_hint_level: postMessage.current_hint_level,
      total_hint_levels: postMessage.total_hint_levels,
      attempt: postMessage.attempt,
    };
    axiosJwt.post(userEventEndpoint, data).catch((err) => logger.error(err));
  };

  const softSlotQuizScore = (
    questionId: string,
    targetId: string,
    orchPayload: OrchardEventPayload
  ) => {
    if (!dbUser) return;

    const orchBody = orchPayload.payload.request.body;
    const score = orchBody.result?.score;
    if (!score) return;

    const orchExt = orchBody.object?.definition?.extensions ?? {};
    const h5pType = orchExt["http://id.tincanapi.com/extension/h5p-type"] as
      | string
      | undefined;

    // Only handle H5P Question Set completions
    if (h5pType !== "Question Set") return;

    const userQuizEndpoint = api + "/user-quiz-submission";

    const data = {
      question_id: questionId,
      target_id: targetId,
      payload: orchPayload,
    };

    axiosJwt
      .post(userQuizEndpoint, data)
      .then((res) => {
        const allScores = res?.data?.allScores;
        if (!Array.isArray(allScores)) {
          console.warn("user-quiz-submission returned unexpected payload", res?.data);
          return;
        }
        setUserQuizSubmissions(removeQuizDupes(allScores));
      })
      .catch((err) => {
        logger.error(err);
      });
  };

  const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshQuizScores = (questionId: string) => {
    const api = process.env.REACT_APP_BACKEND_API;

    // Clear any pending call
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }

    // Schedule a new one
    refreshTimeoutRef.current = setTimeout(() => {
      axiosJwt
        .get(`${api}/quiz-scores/${questionId}`)
        .then((res) => {
          if (res.data) {
            console.log("Quiz refresh: ", res.data);
            setUserQuizSubmissions(removeQuizDupes(res.data.allScores));
          }
        })
        .catch((err) => {
          logger.error(err);
        });
    }, 0);
  };

  const markQuestionStarted = (questionId: string) => {
    const userPostProblemStarted = api + "/user-question-start";
    const data = {
      question_id: questionId,
    };
    axiosJwt
      .post(userPostProblemStarted, data)
      .then((res) => {
        if (res.data.result === false) {
          setUserQuestionStarted([...userQuestionStarted, { question_id: questionId }]);
          // insert questionId into questionStarted array
        }
      })
      .catch((err) => logger.error(err));
  };
  const getFeatureFlagsAndMaintenance = async () => {
    setFeatureFlagEndpointIsLoading(true);
    const api = process.env.REACT_APP_BACKEND_API;
    try {
      axiosJwt.get(api + "/get-feature-flags").then((res) => {
        if (res.data) {
          const mappedFeatureFlags = res.data.featureFlagResult.map(
            (featureFlag: FeatureFlag) => {
              return [featureFlag.id, featureFlag];
            }
          );
          setDbFeatureFlags(new Map(mappedFeatureFlags));
          setMaintenanceWindows(res.data.maintResult ?? []);
          setFeatureFlagEndpointIsLoading(false);
        }
      });
    } catch (err) {
      logger.error(err);
    }
  };

  const checkFeatureFlagAccess = (featureFlagId: number | null) => {
    if (!featureFlagId && DbFeatureFlags) return true;
    if (featureFlagId && DbFeatureFlags) {
      const userFeatureFlags = dbUser?.user_feature_flags;
      const selectedFeatureFlag: FeatureFlag | undefined =
        DbFeatureFlags.get(featureFlagId);

      if (userFeatureFlags && DbFeatureFlags) {
        const check = userFeatureFlags.filter((userFeatureFlag: UserFeatureFlag) => {
          return userFeatureFlag.feature_flag_id === featureFlagId;
        });

        return check.length || selectedFeatureFlag?.released ? true : false;
      } else {
        return selectedFeatureFlag?.released ? true : false;
      }
    } else {
      // feature flag undefined or not found therefore deny access
      return false;
    }
  };

  //TODO Testing required
  const checkModuleCompletion = (moduleId: string) => {
    const moduleProgress = allModulesProgress[moduleId];
    return moduleProgress === 100;
  };

  const checkModuleInProgress = (moduleId: string) => {
    const moduleProgress = allModulesProgress[moduleId];
    return moduleProgress > 0 && moduleProgress < 100;
  };

  const getUserStreak = async (newStreaks: Date[]) => {
    const currentStreaks = [...userStreak];
    if (JSON.stringify(currentStreaks) !== JSON.stringify(newStreaks)) {
      tempEventCtx.setShowStreakAnimation(true);
      tempEventCtx.setShowStreakModal(true);
    }
    setUserStreak(newStreaks);
  };

  const checkPerfectScore = (questionId: string) => {
    if (!questionsSubmitted || !userSubmissions) return false;

    const questionSubmissions = userSubmissions?.filter(
      (submission: UserSubmission) => submission.question_id === questionId
    );

    if (!questionSubmissions) return false;

    const latestSubmission = questionSubmissions.sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    )[0];

    if (!latestSubmission) return false;

    const perfectScore =
      latestSubmission && latestSubmission?.points === latestSubmission?.max_points;
    // const perfectScore = submissions?.filter(
    //   (submission: UserSubmission) =>
    //     submission.points === submission.max_points &&
    // );

    return perfectScore;
  };

  const checkQuestionSubmitted = useCallback(
    (questionId: string) => {
      if (!questionsSubmitted) return false;
      const submissions = userSubmissions?.filter(
        (submission: UserSubmission) => submission.question_id === questionId
      );
      return submissions?.length ? true : false;
    },
    [userSubmissions, questionsSubmitted]
  );

  const checkQuestionTryAgain = useCallback(
    (questionId: string) => {
      if (!tryAgainSubmissions) return false;
      return tryAgainSubmissions.has(questionId);
    },
    [tryAgainSubmissions]
  );

  /**
   * Returns the next question on a target that hasn't been completed
   * Completion means there are no open retries and there are submissions
   * Returns first question if no completed questions.
   * @param targetId string
   * @returns question or undefined
   */
  const getProblemToResumeOnTarget = useCallback(
    (targetId: string) => {
      if (!courseStructureCtx.courseTreeStructure) return;
      const moduleId =
        courseStructureCtx.courseTreeStructure?.getTarget(targetId)?.module_id;
      const module = courseStructureCtx.courseTreeStructure?.getModule(moduleId!);
      if (!module) return;
      const target = courseStructureCtx.courseTreeStructure?.getTarget(targetId);
      if (!target) return;
      let targetQuestions =
        courseStructureCtx.courseTreeStructure?.getAllQuestionsInTarget(targetId);
      // if target complete just return the mastery problem
      if (target.getTargetCompletionStatus() === TARGET_COMPLETION_STATUS.MASTERED) {
        const masteryProblem = targetQuestions.find(
          (question: QuestionTree) => question.type === QuestionType.mastery
        );
        if (masteryProblem) {
          return masteryProblem;
        }
      }
      // return target.questions.find(
      //   (question: Question) => question.type === QuestionType.mastery
      // );

      // go to first incomplete question
      // fall back to first non-perfect question
      // then fall back finally to first question
      return (
        target.getFirstIncompleteQuestion() ||
        target.getFirstNonPerfectQuestion() ||
        target.getFirstQuestion()
      );
    },
    [courseStructureCtx.courseTreeStructure]
  );

  /**
   * Iterates through the targets on current module
   * Finds the first target that has a problem to resume
   * @param moduleId string
   * @returns Question or undefined
   */
  const getProblemToResumeOnModule = useCallback(
    (moduleId: string, defaultToFirst: boolean) => {
      if (!courseStructureCtx.courseTreeStructure) return;

      const module = courseStructureCtx.courseTreeStructure.getModule(moduleId);
      if (!module || module.is_asset_locked) {
        return courseStructureCtx.courseTreeStructure.getFirstQuestionInCourse();
      }

      const moduleTargets =
        courseStructureCtx.courseTreeStructure.getAllTargetsInModule(moduleId) ?? [];

      if (moduleTargets.length === 0) {
        return courseStructureCtx.courseTreeStructure.getFirstQuestionInCourse();
      }

      // Filter out locked targets
      const unlockedTargets = moduleTargets.filter((target) => {
        const fullTarget = courseStructureCtx.courseTreeStructure?.getTarget(target.id);
        return fullTarget && !fullTarget.is_asset_locked;
      });

      // Return early if all unlocked targets are completed
      const allUnlockedTargetsComplete = unlockedTargets.every((target) => {
        const fullTarget = courseStructureCtx.courseTreeStructure?.getTarget(target.id);
        return (
          fullTarget?.getTargetCompletionStatus() === TARGET_COMPLETION_STATUS.MASTERED
        );
      });

      if (unlockedTargets.length > 0 && allUnlockedTargetsComplete && !defaultToFirst) {
        return undefined;
      }

      const nextProblems = unlockedTargets
        .map((target) => {
          const fullTarget = courseStructureCtx.courseTreeStructure?.getTarget(target.id);
          if (!fullTarget) return undefined;

          if (
            fullTarget.getTargetCompletionStatus() === TARGET_COMPLETION_STATUS.MASTERED
          ) {
            return undefined;
          }

          return fullTarget.getFirstIncompleteQuestion();
        })
        .filter(Boolean);

      const firstUnlockedTarget = unlockedTargets[0];

      const firstMastery = firstUnlockedTarget
        ? courseStructureCtx.courseTreeStructure
            .getTarget(firstUnlockedTarget.id)
            ?.getFirstMasteryProblem()
        : undefined;

      const firstProblem = firstUnlockedTarget
        ? courseStructureCtx.courseTreeStructure
            .getTarget(firstUnlockedTarget.id)
            ?.getFirstQuestion()
        : undefined;

      return nextProblems[0] ?? firstMastery ?? firstProblem;
    },
    [courseStructureCtx.courseTreeStructure]
  );

  /**
   * Check if skill video is saved or not
   * @param skillId string
   * @returns true if skill is saved
   */
  const checkSkillSaved = (skillId: string) => {
    return userSavedSkills?.includes(skillId) ?? false;
  };

  /**
   * Toggle a skill video as saved or not
   * @param skillId string
   * @returns void
   */
  const toggleSavedSkill = async (skillId: string) => {
    if (!skillId) return;
    try {
      if (checkSkillSaved(skillId)) {
        // delete saved skill entry
        await axiosJwt.post("/user-saved-skill", {
          skill_id: skillId,
          action: "delete",
        });
        const tempSavedSkills = [...userSavedSkills].filter((entry) => entry !== skillId);
        setUserSavedSkills(tempSavedSkills);
      } else {
        // create saved skill entry
        await axiosJwt.post("/user-saved-skill", {
          skill_id: skillId,
          action: "save",
        });
        const tempSavedSkills = [...userSavedSkills];
        tempSavedSkills.splice(0, 0, skillId);
        setUserSavedSkills(tempSavedSkills);
      }
    } catch (e) {
      logger.error(e);
    }
  };

  //TODO will be changes post re write of course structure
  const passbackTimer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    const getMostRecentSubmissionPoints = (questionId: string, exact?: boolean) => {
      const submissions = userSubmissions?.filter(
        (submission: UserSubmission) => submission.question_id === questionId
      );

      let mostRecentSubmissionPoints = 0;
      let mostRecentSubmissionDate: Date | null = null;

      submissions?.forEach((submission) => {
        const submissionDate = new Date(submission.created_at);

        if (!mostRecentSubmissionDate || submissionDate > mostRecentSubmissionDate) {
          mostRecentSubmissionDate = submissionDate;

          // orchard node override
          if (exact) {
            mostRecentSubmissionPoints = Math.round(
              (submission.points / submission.max_points) * 100
            );
          } else if (submission.points === submission.max_points) {
            // score is 100 when user submitted the problem and got full correct
            mostRecentSubmissionPoints = 100;
          } else {
            // half score when user submitted the problem and got partial correct or wrong
            mostRecentSubmissionPoints = 50;
          }
        }
      });

      return mostRecentSubmissionPoints;
    };

    const getAverageQuizScore = (questionId: string) => {
      const primaryScores =
        userQuizSubmissions?.filter(
          (item) => item.learning_path === "primary" && item.question_id === questionId
        ) ?? [];
      const scores = primaryScores.map((item) => {
        return item.points / item.max_points;
      });
      if (scores.length < 1) return 0;
      const sum = scores.reduce((a, b) => a + b, 0);
      const avg = sum / scores.length || 0;
      return avg;
    };

    const _calculateUserProgress = () => {
      console.warn("Recalculating course tree progress");
      if (!courseStructureCtx.courseTreeStructure) return;
      let allQuestions =
        courseStructureCtx.courseTreeStructure?.getAllQuestionsInCourse() ?? [];
      allQuestions.map((question: QuestionTree) => {
        const hasTryAgain = tryAgainSubmissions.has(question.id);
        let submissionPoints = hasTryAgain
          ? 0
          : getMostRecentSubmissionPoints(question.id);
        if (question.id === "8.EE.C.7.1.2.P3") {
          console.log(`8.EE.C.7.1.2.P3: ${submissionPoints}`);
        }
        // orchard node override
        if (question.activity_type === "orchard_node") {
          question.setProgress(getMostRecentSubmissionPoints(question.id, true));
        } else if (question.type === QuestionType.mastery) {
          question.setProgress(submissionPoints);
        } else {
          // Practice problems receive full credit as long as its attempted
          question.setProgress(submissionPoints > 0 ? 100 : 0);
        }

        question.setScore(getAverageQuizScore(question.id));
        question.setQuestionPerfect(submissionPoints === 100);
        question.setQuestionSubmitted(checkQuestionSubmitted(question.id));
        question.setQuestionRetried(checkQuestionTryAgain(question.id));
        question.setQuestionStarted(
          userQuestionStarted.filter(function (entry) {
            return entry.question_id === question.id;
          }).length > 0
        );
        return "";
      });
      courseStructureCtx.courseTreeStructure?.calculateCourseProgress();
      courseStructureCtx.courseTreeStructure?.calculateCourseScore();
      if (userSubmissions) {
        courseStructureCtxRef.current.setCourseTreeProgressInitialized(true);
      }
    };
    //
    if (currentUserEndpointIsLoading) return;
    if (passbackTimer.current) clearTimeout(passbackTimer.current);
    _calculateUserProgress();
    passbackTimer.current = setTimeout(() => {
      const currentQuestionId = courseStructureCtxRef.current.currentQuestionId;
      if (currentQuestionId) {
        insertUserProgressToCanvas(currentQuestionId);
      }
    }, 1);
    return () => {
      if (passbackTimer.current) clearTimeout(passbackTimer.current);
    };
  }, [
    courseStructureCtx.courseTreeStructure,
    currentUserEndpointIsLoading,
    userSubmissions,
    userQuizSubmissions,
    tryAgainSubmissions,
    tryAgainSubmissions.size,
    userQuestionStarted,
    checkQuestionSubmitted,
    checkQuestionTryAgain,
    insertUserProgressToCanvas,
    studentCurrentClassId,
  ]);

  let getSkillDetailsBySkillIds = async (skillIds: string[]) => {
    if (!skillIds.length) return Promise.resolve([]);
    try {
      const skillDetails = await axiosJwt.get("/skill-details", {
        params: {
          ids: skillIds.join(","),
        },
      });
      return skillDetails.data;
    } catch (e) {
      logger.error(e);
    }
  };

  let getBrowseSkillSections = async () => {
    try {
      const currentCourseId = courseStructureCtx.courseTreeStructure?.getCurrentCourse();
      const currentModuleIds =
        courseStructureCtx.courseTreeStructure?.getAllModuleIdsInCourse() ?? [];
      if (!currentCourseId) return;
      const browseSkillSections = await axiosJwt.get("/browse-skill-sections", {
        params: {
          course_id: currentCourseId.id,
          module_ids: currentModuleIds.join(","), // forces get-explore-videos to limit list of module cards to current tree
        },
      });
      return browseSkillSections.data;
    } catch (e) {
      logger.error(e);
    }
  };

  const currentLtiAssignment = useMemo(() => {
    const { launchedAssignmentId } = authHook;
    return studentCurrentClass?.LtiAssignment?.find(
      (assignment) => assignment.lti_resource_id === launchedAssignmentId
    );
  }, [studentCurrentClass, authHook]);

  useEffect(() => {
    const { courseTreeStructure, courseLoadedSuccessfully } = courseStructureCtx;
    if (!courseTreeStructure || !courseLoadedSuccessfully || !dbUser) return;
    if (dbUser && dbUser.role !== Role.student) return;

    if (currentQuestionId) {
      const currentQuestion = courseTreeStructure.getQuestion(currentQuestionId);
      const courseMetadata = courseTreeStructure.getCurrentCourseMetadata();
      const currentTarget = currentQuestion?.parent as Target | null | undefined;

      if (currentQuestionId === currentLtiAssignment?.digit_resource_id) return;

      if (
        currentQuestion &&
        courseMetadata.use_digit_study_tool &&
        currentTarget?.is_asset_locked &&
        !courseMetadata.use_digit_study_tool_unlocked
      ) {
        ErrorToast("The lesson you're trying to access is currently locked.");
        history.push("/learning-overview");
      }
    }
  }, [
    courseStructureCtx,
    currentLtiAssignment?.digit_resource_id,
    currentQuestionId,
    dbUser,
    history,
  ]);

  //This useEffect is to lock/unlock assets for Digit as a study tool
  useEffect(() => {
    try {
      if (!dbUser) return;
      console.info(
        "ASSIGNMENT: Scanning through tree for lock status. Current assignment:",
        currentLtiAssignment
      );

      const setTargetLockStatus = (
        lock: boolean,
        module: ModuleTree,
        overrideLock?: boolean
      ) => {
        module.children.forEach((target) =>
          target.setTargetLockStatus(lock, overrideLock)
        );
      };

      const courseTreeStructure = courseStructureCtx.courseTreeStructure;
      const currentCourseModules = courseTreeStructure?.getAllModulesInCourse();
      const courseMetadata = courseTreeStructure?.getCurrentCourseMetadata();
      const lockOverride =
        assetLockOverride || courseMetadata?.use_digit_study_tool_unlocked;

      console.info(
        `ASSIGNMENT: Lock override: ${lockOverride}, ${assetLockOverride}, ${courseMetadata?.use_digit_study_tool_unlocked}`
      );

      // First: lock everything
      if (currentLtiAssignment && !lockOverride) {
        console.info("ASSIGNMENT: Resetting assignment locks");
        currentCourseModules?.forEach((mod) => {
          mod.setModuleLockStatus(true, lockOverride);
          setTargetLockStatus(true, mod, lockOverride);
        });
      }

      // Case: no launched assignment → unlock everything
      if (!currentLtiAssignment || lockOverride) {
        console.info("ASSIGNMENT: No launched assignment ID: Unlocking all");
        currentCourseModules?.forEach((mod) => {
          mod.setModuleLockStatus(false, lockOverride);
          setTargetLockStatus(false, mod, lockOverride);
        });
        return;
      }

      // Case: not higher-ed or user isn't a student → unlock everything
      if (
        studentCurrentClass?.course?.higher_ed === false ||
        dbUser?.role !== Role.student
      ) {
        console.info(
          "ASSIGNMENT: Course isn't higher ed or user is not a student: Unlocking all"
        );
        currentCourseModules?.forEach((mod) => {
          mod.setModuleLockStatus(false, lockOverride);
          setTargetLockStatus(false, mod, lockOverride);
        });
        return;
      }

      // If no LTI assignment records at all → do nothing (remain locked)
      if (!studentCurrentClass?.LtiAssignment) return;

      let currentModuleId: string | null;
      let currentModule: ModuleTree | undefined | null;
      const currentTime = new Date();

      // Iterate through all class assignments to check due dates
      console.info(
        "ASSIGNMENT: checking each assignment in: ",
        studentCurrentClass.LtiAssignment
      );

      studentCurrentClass.LtiAssignment.forEach((assignment) => {
        const metadata = assignment.assignment_metadata;

        const studentDueDate =
          metadata?.studentDueDates &&
          dbUser?.lti_user_id &&
          metadata.studentDueDates[dbUser.lti_user_id]
            ? metadata.studentDueDates[dbUser.lti_user_id]
            : undefined;

        // Per-student due date
        console.info("ASSIGNMENT: student due date unlocking");
        if (metadata?.studentDueDates && dbUser?.lti_user_id && studentDueDate) {
          const dueDate = new Date(studentDueDate);
          if (currentTime > dueDate) {
            if (assignment.digit_resource_type?.toLowerCase() === "module") {
              const thisModule = currentCourseModules?.find(
                (mod) => mod.id === assignment.digit_resource_id
              );
              thisModule?.setModuleLockStatus(false, lockOverride);
              thisModule && setTargetLockStatus(false, thisModule, lockOverride);
            } else if (assignment.digit_resource_type?.toLowerCase() === "target") {
              const thisModule = courseTreeStructure?.getModuleByQuestionId(
                assignment.digit_resource_id as string
              );
              thisModule?.setModuleLockStatus(false, lockOverride);

              const thisTarget = courseTreeStructure?.getTargetByQuestionId(
                assignment.digit_resource_id as string
              );
              thisTarget && thisTarget.setTargetLockStatus(false, lockOverride);
            }
          }
        }

        // Fallback dueDateCheck gate
        console.info("ASSIGNMENT: due date unlocking");
        if (metadata?.dueDateCheck && currentTime > new Date(metadata?.dueDateCheck)) {
          if (assignment.digit_resource_type?.toLowerCase() === "module") {
            const thisModule = currentCourseModules?.find(
              (mod) => mod.id === assignment.digit_resource_id
            );
            thisModule?.setModuleLockStatus(false, lockOverride);
            thisModule && setTargetLockStatus(false, thisModule, lockOverride);
          } else if (assignment.digit_resource_type?.toLowerCase() === "target") {
            const thisModule = courseTreeStructure?.getModuleByQuestionId(
              assignment.digit_resource_id as string
            );
            thisModule?.setModuleLockStatus(false, lockOverride);

            const thisTarget = courseTreeStructure?.getTargetByQuestionId(
              assignment.digit_resource_id as string
            );
            thisTarget && thisTarget.setTargetLockStatus(false, lockOverride);
          }
        }
      });

      // Unlocking based on the launched assignment
      console.info("ASSIGNMENT: launched assignment unlocking");

      if (currentLtiAssignment?.digit_resource_type?.toLowerCase() === "module") {
        currentModuleId = currentLtiAssignment.digit_resource_id;
        currentModule = currentCourseModules?.find((mod) => mod.id === currentModuleId);
        console.log("ASSIGNMENT: Unlocking module: ", currentModuleId, currentModule?.id);

        const currentModuleIndex = currentModule
          ? currentCourseModules?.indexOf(currentModule)
          : undefined;

        // Unlock the launched module and its targets
        currentModule?.setModuleLockStatus(false, lockOverride);
        currentModule && setTargetLockStatus(false, currentModule, lockOverride);

        // Unlock current and all previous modules (+ their targets)
        if (
          currentCourseModules &&
          currentModuleIndex !== undefined &&
          currentModuleIndex >= 0
        ) {
          for (let i = 0; i <= currentModuleIndex; i++) {
            const module = currentCourseModules[i];
            module.setModuleLockStatus(false, lockOverride);
            setTargetLockStatus(false, module, lockOverride);
          }
        }

        // Unlock completed modules OR modules that have any started targets
        currentCourseModules?.forEach((mod) => {
          if (mod.isCompleted?.() || mod.isModuleStarted?.()) {
            mod.setModuleLockStatus(false, lockOverride);
            setTargetLockStatus(false, mod, lockOverride);
          }
        });
      } else if (currentLtiAssignment?.digit_resource_type?.toLowerCase() === "target") {
        // Target-level launch
        currentModule = courseTreeStructure?.getModuleByQuestionId(
          currentLtiAssignment.digit_resource_id as string
        );

        // Unlock containing module
        currentModule?.setModuleLockStatus(false, lockOverride);

        // Unlock the launched target (IDs at target level are question IDs)
        const currentModuleIndex = courseTreeStructure?.root.children.indexOf(
          currentModule as any
        );
        const currentModuleTargets = currentModule?.children as Target[] | undefined;

        const currentTarget = courseTreeStructure?.getTargetByQuestionId(
          currentLtiAssignment.digit_resource_id as string
        );
        const currentTargetIndex = currentModule?.children.indexOf(currentTarget as any);

        currentTarget && currentTarget.setTargetLockStatus(false, lockOverride);

        console.log(
          "ASSIGNMENT: Unlocking target: ",
          currentLtiAssignment.digit_resource_id,
          currentTarget?.id
        );

        // Unlock previous targets in this module
        if (currentTargetIndex !== undefined && currentTargetIndex >= 0) {
          for (let i = 0; i <= currentTargetIndex; i++) {
            const target = currentModule?.children[i] as Target | undefined;
            target?.setTargetLockStatus(false, lockOverride);
          }
        }

        // Unlock all previous modules (and their targets)
        if (currentModuleIndex !== undefined && currentModuleIndex >= 0) {
          for (let i = 0; i < currentModuleIndex; i++) {
            const module = courseTreeStructure?.root.children[i] as ModuleTree;
            module.setModuleLockStatus(false, lockOverride);
            for (let ii = 0; ii < module.children.length; ii++) {
              const target = module.children[ii] as Target;
              target.setTargetLockStatus(false, lockOverride);
            }
          }
        }

        // Unlock completed OR started targets in the current module
        currentModuleTargets?.forEach((targ) => {
          if (targ.isCompleted?.() || targ.isTargetStarted?.()) {
            targ.setTargetLockStatus(false, lockOverride);
          }
        });
      }

      // --- Final sweep: auto-unlock based on "started" status everywhere ---
      // Any started target unlocks itself AND its parent module.
      // Any module that is "started" (by your implementation) unlocks as well.
      console.info("ASSIGNMENT: Final sweep started status unlocking");
      currentCourseModules?.forEach((mod) => {
        let anyStartedTarget = false;

        mod.children?.forEach((targ: any) => {
          if (targ?.isTargetStarted?.() === true) {
            targ.setTargetLockStatus(false, lockOverride);
            anyStartedTarget = true;
          }
        });

        if (anyStartedTarget || mod.isModuleStarted?.() === true) {
          mod.setModuleLockStatus(false, lockOverride);
        }
      });
    } catch (e) {
      console.error("Error processing assignment unlocks: ", e);
    }
  }, [
    currentLtiAssignment,
    currentLtiAssignment?.digit_resource_id,
    currentLtiAssignment?.digit_resource_type,
    courseStructureCtx.courseTreeStructure,
    dbUser,
    studentCurrentClass?.LtiAssignment,
    studentCurrentClass?.course?.higher_ed,
    assetLockOverride,
  ]);

  const calculateLearningMapProgress = useCallback(
    (targets: Target[]): ComputedQuizScore => {
      if (!targets?.length) {
        return {
          points: 0,
          max_points: 0,
          percentage: null,
          display_as: "points",
          invalid: "No Quiz",
        };
      }

      const firstTarget = targets[0];
      const targetCompleted = firstTarget.isCompleted();

      const allTargetIds = targets.map((t) => t.id);

      const targetQuizScores =
        userQuizSubmissions?.filter(
          (item) =>
            allTargetIds.includes(item.target_id) && item.learning_path === "primary"
        ) ?? [];

      const totalExpectedQuizzes = targets.reduce((sum, t) => {
        const meta: QuestionMetadata = t.getQuestionMetadata();
        return sum + (meta.primary_quizzes ?? 0);
      }, 0);

      if (totalExpectedQuizzes === 0) {
        return {
          points: 0,
          max_points: 0,
          percentage: null,
          display_as: "points",
          invalid: "No Quiz",
        };
      }

      // We cap points so it doesn't exceed max_points (extra safety if dupes slip through).
      const points = Math.min(targetQuizScores.length, totalExpectedQuizzes);
      const max_points = totalExpectedQuizzes;

      // If nothing submitted and target not completed, mirror the old "-" behavior
      if (points === 0 && !targetCompleted) {
        return {
          points: 0,
          max_points,
          percentage: null,
          display_as: "points",
          invalid: "-",
        };
      }

      return {
        points,
        max_points,
        percentage: max_points > 0 ? points / max_points : null,
        display_as: "points",
      };
    },
    [userQuizSubmissions]
  );

  const calculateQuizScore = useCallback(
    (targets: Target[]): ComputedQuizScore => {
      if (!targets?.length) {
        return {
          points: 0,
          max_points: 100,
          percentage: null,
          display_as: "percentage",
          invalid: "No Quiz",
        };
      }

      const firstTarget = targets[0];
      const targetCompleted = firstTarget.isCompleted();
      const module = firstTarget.parent as ModuleTree | null;
      const course = module?.parent as Course | null;
      const courseMetadata = course?.getMetadata() as CourseMetadata | null;

      if (
        courseMetadata?.lti_grading_method === "completion" ||
        !courseMetadata?.lti_grading_method
      ) {
        const allTargetIds = targets.map((t) => t.id);
        const targetQuizScores =
          userQuizSubmissions?.filter(
            (item) =>
              allTargetIds.includes(item.target_id) && item.learning_path === "primary"
          ) ?? [];

        const totalExpectedQuizzes = targets.reduce((sum, t) => {
          const meta: QuestionMetadata = t.getQuestionMetadata();
          return sum + (meta.primary_quizzes ?? 0);
        }, 0);

        if (totalExpectedQuizzes === 0) {
          return {
            points: 0,
            max_points: 100,
            percentage: null,
            display_as: "percentage",
            invalid: "No Quiz",
          };
        }

        if (!targetQuizScores.length || !targetCompleted) {
          return {
            points: 0,
            max_points: 100,
            percentage: null,
            display_as: "percentage",
            invalid: "-",
          };
        }

        const earnedPercentage = targetQuizScores.reduce((sum, submission) => {
          const points = submission.points ?? 0;
          const maxPoints = submission.max_points ?? 0;
          const percent = maxPoints > 0 ? points / maxPoints : 0;
          return sum + percent;
        }, 0);

        const averagePercentage = earnedPercentage / totalExpectedQuizzes;
        const points = Math.round(averagePercentage * 100);
        const max_points = 100;

        return {
          points,
          max_points,
          percentage: points / max_points,
          display_as: "percentage",
        };
      } else {
        const gradedQuestions = targets.flatMap((t) =>
          (t.children as QuestionTree[]).filter((q) => q.graded_quiz_id !== null)
        );

        const totalQuizzes = gradedQuestions.length;

        if (totalQuizzes === 0) {
          return {
            points: 0,
            max_points: 100,
            percentage: null,
            display_as: "points",
            invalid: "No Quiz",
          };
        }

        const max_points = gradedQuestions.reduce(
          (sum, question) => sum + (question.graded_quiz_max_points || 0),
          0
        );

        const quizIds = gradedQuestions
          .map((q) => q.graded_quiz_id)
          .filter((id): id is string => id !== null);

        const relevantSubmissions = userQuizSubmissions?.filter(
          (sub) => sub.learning_path === "primary" && quizIds.includes(sub.quiz_id)
        );

        if (!relevantSubmissions?.length && !targetCompleted) {
          return {
            points: 0,
            max_points: 100,
            percentage: null,
            display_as: "points",
            invalid: "-",
          };
        }

        const points =
          relevantSubmissions?.reduce((sum, sub) => sum + (sub.points || 0), 0) ?? 0;

        return {
          points,
          max_points,
          percentage: points / max_points,
          display_as: "points",
        };
      }
    },
    [userQuizSubmissions]
  );

  const maintenanceWindow = useMemo<MaintenanceWindow | undefined>(() => {
    return maintenanceWindows?.[0];
  }, [maintenanceWindows]);

  return (
    <UserContext.Provider
      value={{
        dbUser,
        studentCurrentClass,
        userStreak,
        userSubmissions,
        userQuizSubmissions,
        questionsSubmitted,
        tryAgainSubmissions,
        allModulesProgress,
        DbFeatureFlags,
        currentUserEndpointIsLoading,
        tryAgainEndpointIsLoading,
        updateUserEndpointIsLoading,
        patchUserLastAccessAtEndpointIsLoading,
        featureFlagEndpointIsLoading,
        assetLockOverride,
        setAllModulesProgress,
        getFeatureFlagsAndMaintenance,
        checkFeatureFlagAccess,
        setDbUser,
        getDbUser,
        getUserStreak,
        updateDbUser,
        updateUserMetadata,
        setUserSubmissions,
        setUserQuizSubmissions,
        setQuestionsSubmitted,
        checkPerfectScore,
        setTryAgainSubmissions,
        upsertUserProgress,
        insertUserSubmission,
        insertUserEvents,
        insertUserEventsV2,
        markQuestionStarted,
        insertUserTryAgain,
        deleteUserTryAgain,
        getAllTryAgainQuestions,
        setCurrentUserEndpointIsLoading,
        getProblemToResumeOnTarget,
        checkQuestionSubmitted,
        checkQuestionTryAgain,
        refreshQuizScores,
        softSlotQuizScore,
        getProblemToResumeOnModule,
        checkModuleCompletion,
        checkModuleInProgress,
        checkSkillSaved,
        toggleSavedSkill,
        userSavedSkills,
        getSkillDetailsBySkillIds,
        getBrowseSkillSections,
        refreshExplorePageData,
        setRefreshExplorePageData,
        upsertUserSurvey,
        gppTextMode,
        setGppTextMode,
        resetGppTextMode,
        getProfileClassDetails,
        upsertUserClassSettings,
        insertUserProgressToCanvas,
        patchDbUserLastAccessAt,
        handleChangeStudentClass,
        calculateQuizScore,
        calculateLearningMapProgress,
        currentLtiAssignment,
        authHook,
        maintenanceWindow,
        setAssetLockOverride,
      }}>
      {children}
    </UserContext.Provider>
  );
};
